import { DetailTranslationKeys, SettingsKeys, SettingsTranslationKeys } from '../../../../api/models/TranslationKeys';
import { Backend } from '../../../../api/Backend';
import { GlobalSettingsKeys } from '../../../../api/models/GlobalSettingsKeys';

export const enum SubscriptionTypes {
  PROGRAM = 'this_program', //=== episode
  SEASON = 'this_season',
  SERIES = 'this_series',
}

const defaultBeginBufferFallback = 300000;
const defaultEndBufferFallback = 900000;

export class SubscriptionSettings {
  deviceId: string | undefined;
  type: SubscriptionTypes;
  beginBuffer: number;
  endBuffer: number;
  deleteProtected: boolean;
  subscriptionTypes: { value: SubscriptionTypes; text: string }[] = [];
  defaultTypeIndex = -1;
  beginBufferOptions: { text: string; value: string }[] = [];
  defaultBeginIndex = -1;
  endBufferOptions: { text: string; value: string }[] = [];
  defaultEndIndex = -1;
  titles: { deleteProtectTitle: string; beginBufferTitle: string; typeTitle: string; endBufferTitle: string } = {
    deleteProtectTitle: '',
    beginBufferTitle: '',
    typeTitle: '',
    endBufferTitle: '',
  };
  onText = '';
  offText = '';
  private onlyProgram: boolean;

  constructor(
    backend: Backend,
    deviceId: string | undefined,
    type = SubscriptionTypes.PROGRAM,
    beginBuffer?: number,
    endBuffer?: number,
    deleteProtected?: boolean,
    onlyProgram = false
  ) {
    this.deviceId = deviceId;
    this.type = type;
    const defaultBeginBuffer = backend.globalSettings.numberValue(GlobalSettingsKeys.recordingDefaultBeforeTime);
    const defaultEndBuffer = backend.globalSettings.numberValue(GlobalSettingsKeys.recordingDefaultAfterTime);
    const defaultProtectedString = backend.globalSettings.stringValue(
      GlobalSettingsKeys.recordingDefaultDeleteProtection
    );
    let defaultProtected = false;
    if (defaultProtectedString === 'ON') {
      defaultProtected = true;
    }
    if (beginBuffer !== undefined) {
      this.beginBuffer = beginBuffer;
    } else {
      if (defaultBeginBuffer !== undefined) {
        this.beginBuffer = defaultBeginBuffer * 60000;
      } else {
        this.beginBuffer = defaultBeginBufferFallback;
      }
    }
    if (endBuffer !== undefined) {
      this.endBuffer = endBuffer;
    } else {
      if (defaultEndBuffer !== undefined) {
        this.endBuffer = defaultEndBuffer * 60000;
      } else {
        this.endBuffer = defaultEndBufferFallback;
      }
    }
    this.deleteProtected = deleteProtected ? deleteProtected : defaultProtected;
    this.onlyProgram = onlyProgram;

    this.initOptions(backend);
  }

  initOptions(backend: Backend) {
    if (this.onlyProgram) {
      this.subscriptionTypes = [
        {
          value: SubscriptionTypes.PROGRAM,
          text: backend.translations.forKey(DetailTranslationKeys.detail_recording_this_episode),
        },
      ];
    } else {
      this.subscriptionTypes = [
        {
          value: SubscriptionTypes.PROGRAM,
          text: backend.translations.forKey(DetailTranslationKeys.detail_recording_this_episode),
        },
        {
          value: SubscriptionTypes.SEASON,
          text: backend.translations.forKey(DetailTranslationKeys.detail_recording_this_season),
        },
        {
          value: SubscriptionTypes.SERIES,
          text: backend.translations.forKey(DetailTranslationKeys.detail_recording_this_series),
        },
      ];
    }
    this.defaultTypeIndex = this.subscriptionTypes.findIndex(val => val.value === this.type);
    if (this.defaultTypeIndex === -1) {
      // if none matching can be found fallback to first value
      this.defaultTypeIndex = 0;
      this.type = SubscriptionTypes.PROGRAM;
    }

    const minutesStr = backend.translations.forKey(DetailTranslationKeys.detail_duration_min);

    let beginBuffers = backend.globalSettings.stringArray(SettingsKeys.recordingBeforeTimes);
    beginBuffers = beginBuffers ? beginBuffers : ['0', '5', '10', '15', '20', '30'];
    this.beginBufferOptions = beginBuffers.map(option => {
      return { value: '' + parseInt(option, 10) * 60000, text: `${option} ${minutesStr}` };
    });
    this.defaultBeginIndex = this.beginBufferOptions.findIndex(val => val.value === this.beginBuffer.toString());
    if (this.defaultBeginIndex === -1) {
      // if none matching can be found fallback to first value
      this.defaultBeginIndex = 0;
      this.beginBuffer = 0;
    }

    let endBuffers = backend.globalSettings.stringArray(SettingsKeys.recordingAfterTimes);
    endBuffers = endBuffers ? endBuffers : ['0', '5', '10', '15', '20', '30'];
    this.endBufferOptions = endBuffers.map(option => {
      return { value: '' + parseInt(option, 10) * 60000, text: `${option} ${minutesStr}` };
    });
    this.defaultEndIndex = this.endBufferOptions.findIndex(val => val.value === this.endBuffer.toString());
    if (this.defaultEndIndex === -1) {
      // if none matching can be found fallback to first value
      this.defaultEndIndex = 0;
      this.endBuffer = 0;
    }

    this.titles = {
      typeTitle: backend.translations.forKey(DetailTranslationKeys.detail_recording_episodes),
      beginBufferTitle: backend.translations.forKey(DetailTranslationKeys.detail_recording_change_rec_option_before),
      endBufferTitle: backend.translations.forKey(DetailTranslationKeys.detail_recording_change_rec_option_after),
      deleteProtectTitle: backend.translations.forKey(DetailTranslationKeys.detail_recording_delete_protected),
    };

    this.onText = backend.translations.forKey(SettingsTranslationKeys.settings_toggle_on);
    this.offText = backend.translations.forKey(SettingsTranslationKeys.settings_toggle_off);
  }
}
